import { CloseSquare, Sms, Trash } from "iconsax-react";
import { getDOB } from "../../../utils/calculateAge";
import { useState } from "react";
import { getToken } from "../../../utils/token";
import { toast } from "react-toastify";
import instance from "../../../axios/instance";
import { useRevalidator } from "react-router-dom";
import { ConfirmationModal } from "./ConfirmationModal";

export const DoctorItem = ({ doctor, onClose, deactivateActions, currentDoctor }) => {
  const [deactivateDoctor, setDeactivateDoctor] = useState(null);
  const [makeDoctorAdmin, setMakeDoctorAdmin] = useState(null);
  const [deleteDoctor, setDeleteDoctor] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [adminLoading, setAdminLoading] = useState(false);
  const [deactivateLoading, setDeactivateLoading] = useState(false);
  const [sendingInvite, setSendingInvite] = useState(false);
  const [specialty, setSpecialty] = useState(doctor.specialty || "");
  const token = getToken();
  const revalidator = useRevalidator();

  const handleDeleteDoctor = async (doc) => {
    setDeleteLoading(true);
    try {
      await instance.post(
        "/doctor/delete",
        { doctorId: doc.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success("Member deleted");
      window.location.reload();
    } catch (error) {
      setDeleteLoading(false);
      if (error?.response?.status === 401) {
        return toast.error("You are not authorized for this");
      }
      toast.error("An unknown error occurred. Please try again");
      console.log(error);
    }
  };

  const handleDeactivateDoctor = async (doc) => {
    setDeactivateLoading(true);
    try {
      const res = await instance.put(
        "/doctor/deactivate-toggle",
        { doctorId: doc.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(res.data.message);
      window.location.reload();
    } catch (error) {
      setDeactivateLoading(false);
      if (error?.response?.status === 401) {
        return toast.error("You are not authorized for this");
      }
      toast.error(error?.response?.data?.message, { autoClose: false });
      console.log(error);
    }
  };

  const handleAdminDoctor = async (doc) => {
    setAdminLoading(true);
    try {
      const res = await instance.put(
        "/doctor/admin-toggle",
        { doctorId: doc.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(res.data.message);
      window.location.reload();
    } catch (error) {
      setAdminLoading(false);
      if (error?.response?.status === 401) {
        return toast.error("You are not authorized for this");
      }
      toast.error("An unknown error occurred. Please try again");
      console.log(error);
    }
  };

  const handleSpecialtyChange = async (specialty) => {
    if (!specialty) return toast.error('A specialty needs to be specified')
    setSpecialty(specialty);
    try {
      const res = await instance.put(
        "/doctor/edit/specialty",
        { doctorId: doctor.id, specialty },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      toast.success(res.data.message);
      revalidator.revalidate();
    } catch (error) {
      if (error?.response?.status === 401) {
        return toast.error("You are not authorized for this");
      }
      toast.error("An unknown error occurred. Please try again");
      console.log(error);
    }
  };

  const sendNewInviteLink = async (doc) => {
    setSendingInvite(true);
    try {
      const res = await instance.post(
        "/doctor/doctors/resend-invite",
        { email: doc.email },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setSendingInvite(false);
      toast.success(res.data.message);
    } catch (error) {
      setSendingInvite(false);
      if (error?.response?.status === 401) {
        return toast.error("You are not authorized for this");
      }
      toast.error("An unknown error occurred. Please try again");
      console.log(error);
    }
  };

  return (
    <div className="fixed top-0 right-0 h-[100vh] w-[450px] bg-white px-3 py-10 overflow-y-scroll scrollbar-hide z-30">
      <div className="flex items-center justify-between">
        <div className="text-[18px] font-medium select-none">
          Member Information - {doctor.id}
        </div>
        <button onClick={onClose}>
          <CloseSquare variant="Bold" />
        </button>
      </div>
      <div className="border rounded-md px-3 py-3 mt-5 grid grid-cols-2 items-start gap-x-5 gap-y-5">
        <div>
          <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
            Name:
          </h2>
          <p className="text-xs">
            {doctor.prefix} {doctor.lastname} {doctor.firstname}
          </p>
        </div>
        <div>
          <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
            Email:
          </h2>
          <p className="text-xs break-all">{doctor.email}</p>
        </div>
        <div>
          <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
            Gender:
          </h2>
          <p className="text-xs">{doctor.gender}</p>
        </div>
        <div>
          <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
            Birthday:
          </h2>
          <p className="text-xs">{getDOB(doctor.dob, true)}</p>
        </div>
        <div>
          <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
            Degree:
          </h2>
          <p className="text-xs">{doctor.degree}</p>
        </div>
        <div>
          <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
            Specialization:
          </h2>
          <p className="text-xs">{doctor.specialization}</p>
        </div>
        <div>
          <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
            License number:
          </h2>
          <p className="text-xs">{doctor.license_number}</p>
        </div>
        <div>
          <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
            DEA number:
          </h2>
          <p className="text-xs">{doctor.dea_number}</p>
        </div>
        <div>
          <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
            NPI number:
          </h2>
          <p className="text-xs">{doctor.npi_number}</p>
        </div>
        <div>
          <h2 className="text-sm mb-1 capitalize text-[black] font-[500]">
            Role:
          </h2>
          <p className="text-xs">
            {doctor.access === "doc" ? "Member" : "Admin"}
          </p>
        </div>
      </div>
      {currentDoctor.access !== "doc" && (
        <div className="border rounded-md px-3 py-3 mt-5">
          <label htmlFor="specialty">
            Update the specialty role for this user
          </label>
          <select
            name="specialty"
            id="specialty"
            className={`block border mt-3 text-sm border-gray-300 focus:border-primary duration-300 bg-inherit px-2 py-1 w-full rounded-[4px]`}
            placeholder="Select specialty role"
            value={specialty}
            onChange={(e) => handleSpecialtyChange(e.target.value)}
          >
            <option value="">Select specialty role</option>
            <option value="doctor">Doctor/Nurse</option>
            {/* <option value="nurse">Nurse</option> */}
            <option value="worker">Staff</option>
          </select>
        </div>
      )}
      {!deactivateActions && (
        <div className="mt-5">
          {doctor.account_status !== "inactive" && (
            <div className="grid grid-cols-2 gap-x-4 mb-5">
              <button
                className={`bg-white w-full h-9 hover:border-primary border-primary duration-300 cursor-pointer border text-sm flex justify-center items-center rounded-md disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[#eee]`}
                onClick={() => setMakeDoctorAdmin(doctor)}
              >
                {adminLoading
                  ? "processing..."
                  : doctor.access === "doc"
                  ? "Make Admin"
                  : "Remove as Admin"}
              </button>
              <button
                className={`bg-white w-full h-9 hover:border-primary border-primary duration-300 cursor-pointer border text-sm flex justify-center items-center rounded-md disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[#eee]`}
                onClick={() => setDeactivateDoctor(doctor)}
              >
                {deactivateLoading
                  ? "processing..."
                  : doctor.account_status !== "deactivated"
                  ? "Deactivate"
                  : "Activate"}
              </button>
            </div>
          )}
          {doctor.account_status === "inactive" && (
            <button
              className={`bg-white w-full h-9 hover:border-primary border-primary duration-300 cursor-pointer border text-sm flex justify-center items-center rounded-md disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[rgb(238,238,238)] mb-5`}
              onClick={() => sendNewInviteLink(doctor)}
            >
              <Sms size="17" color="black" style={{ marginRight: "10px" }} />
              {sendingInvite ? "processing..." : "Send new invite link"}
            </button>
          )}
          <button
            className={`bg-white w-full h-9 hover:border-[tomato] border-[tomato] duration-300 cursor-pointer border text-sm flex justify-center items-center rounded-md disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:border-[#eee]`}
            onClick={() => setDeleteDoctor(doctor)}
          >
            <Trash size="17" color="black" style={{ marginRight: "10px" }} />
            {deleteLoading ? "processing..." : "Delete Member"}
          </button>
        </div>
      )}

      {deleteDoctor && (
        <>
          <ConfirmationModal
            onClose={() => setDeleteDoctor(null)}
            cta={() => handleDeleteDoctor(deleteDoctor)}
            msg={"Are you sure you want to delete this member?"}
          />
          <div
            className="fixed z-20 inset-0 bg-[rgba(0,0,0,.2)]"
            onClick={() => setDeleteDoctor(null)}
          />
        </>
      )}
      {deactivateDoctor && (
        <>
          <ConfirmationModal
            onClose={() => setDeactivateDoctor(null)}
            cta={() => handleDeactivateDoctor(deactivateDoctor)}
            msg={"Are you sure?"}
          />
          <div
            className="fixed z-20 inset-0 bg-[rgba(0,0,0,.2)]"
            onClick={() => setDeactivateDoctor(null)}
          />
        </>
      )}
      {makeDoctorAdmin && (
        <>
          <ConfirmationModal
            onClose={() => setMakeDoctorAdmin(null)}
            cta={() => handleAdminDoctor(makeDoctorAdmin)}
            msg={"Are you sure?"}
          />
          <div
            className="fixed z-20 inset-0 bg-[rgba(0,0,0,.2)]"
            onClick={() => setMakeDoctorAdmin(null)}
          />
        </>
      )}
    </div>
  );
};

